import {lazy, FC, Suspense, useEffect} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {useAuth} from '../modules/auth'
import {usePageContext} from '../../_metronic/layout/core/PageProvider'
import PaymentSettingsPage from '../modules/apps/payment-setting/PaymentSettingsPage'

const PrivateRoutes = () => {
  const DashboardWrapper = lazy(() => import('../pages/dashboard/DashboardWrapper1'))
  const AccountPage = lazy(() => import('../modules/apps/profile/AccountPage'))
  const AcademicYearsPage = lazy(() => import('../modules/apps/academic-year/AcademicYearsPage'))
  const AccountsPage = lazy(() => import('../modules/apps/account/AccountsPage'))
  const AdmissionsPage = lazy(() => import('../modules/apps/admission/AdmissionsPage'))
  const AdmissionFieldsPage = lazy(
    () => import('../modules/apps/admission-field/AdmissionFieldsPage')
  )
  const AssetCategorysPage = lazy(() => import('../modules/apps/asset-category/AssetCategorysPage'))
  const AssetItemsPage = lazy(() => import('../modules/apps/asset-item/AssetItemsPage'))
  const AssetIssuesPage = lazy(() => import('../modules/apps/asset-issue/AssetIssuesPage'))
  const AssetPurchasesPage = lazy(() => import('../modules/apps/asset-purchase/AssetPurchasesPage'))
  const AssetStoresPage = lazy(() => import('../modules/apps/asset-store/AssetStoresPage'))
  const AssignmentSubmissionsPage = lazy(
    () => import('../modules/apps/assignment-submission/AssignmentSubmissionsPage')
  )
  const AssignmentsPage = lazy(() => import('../modules/apps/assignment/AssignmentsPage'))
  const BooksPage = lazy(() => import('../modules/apps/book/BooksPage'))
  const BookIssuesPage = lazy(() => import('../modules/apps/book-issue/BookIssuesPage'))
  const ClasssPage = lazy(() => import('../modules/apps/class/ClasssPage'))
  const ComplainsPage = lazy(() => import('../modules/apps/complain/ComplainsPage'))
  const DepartmentsPage = lazy(() => import('../modules/apps/department/DepartmentsPage'))
  const DesignationsPage = lazy(() => import('../modules/apps/designation/DesignationsPage'))
  const IncidentCategorysPage = lazy(
    () => import('../modules/apps/incident-category/IncidentCategorysPage')
  )
  const EmployeeAttendancesPage = lazy(
    () => import('../modules/apps/employee-attendance/EmployeeAttendancesPage')
  )
  const EmployeesPage = lazy(() => import('../modules/apps/employee/EmployeesPage'))
  const ExamAttendancesPage = lazy(
    () => import('../modules/apps/exam-attendance/ExamAttendancesPage')
  )
  const ExamPsychomotorsPage = lazy(() => import('../modules/apps/psychomotor/PsychomotorsPage'))
  const ExamResultsPage = lazy(() => import('../modules/apps/exam-result/ExamResultsPage'))
  const ExamSchedulesPage = lazy(() => import('../modules/apps/exam-schedule/ExamSchedulesPage'))
  const ExamsPage = lazy(() => import('../modules/apps/exam/ExamsPage'))
  const ExamInstructionsPage = lazy(
    () => import('../modules/apps/exam-instruction/ExamInstructionsPage')
  )
  const ExamOnlineExamsPage = lazy(
    () => import('../modules/apps/exam-online-exam/ExamOnlineExamsPage')
  )
  const ExamQuestionsPage = lazy(() => import('../modules/apps/exam-question/ExamQuestionsPage'))
  const ExamTakenExamsPage = lazy(
    () => import('../modules/apps/exam-taken-exam/ExamTakenExamsPage')
  )
  const TakeExamPage = lazy(
    () => import('../modules/apps/exam-online-exam/exam-online-exams-list/takeExam/index')
  )
  const ExpendituresPage = lazy(() => import('../modules/apps/expenditure/ExpendituresPage'))
  const DiscountsPage = lazy(() => import('../modules/apps/discount/DiscountsPage'))
  const InvoicesPage = lazy(() => import('../modules/apps/invoice/InvoicesPage'))
  const FeeTypesPage = lazy(() => import('../modules/apps/fee-type/FeeTypesPage'))
  const GallerysPage = lazy(() => import('../modules/apps/gallery/GallerysPage'))
  const FinalResultsPage = lazy(() => import('../modules/apps/final-result/FinalResultsPage'))
  const ExamGradesPage = lazy(() => import('../modules/apps/grade/GradesPage'))
  const HostelMembersPage = lazy(() => import('../modules/apps/hostel-member/HostelMembersPage'))
  const HostelsPage = lazy(() => import('../modules/apps/hostel/HostelsPage'))
  const RoomsPage = lazy(() => import('../modules/apps/room/RoomsPage'))
  const MarksPage = lazy(() => import('../modules/apps/mark/MarksPage'))
  const MarksheetsPage = lazy(() => import('../modules/apps/marksheet/MarksheetsPage'))
  const NewsPage = lazy(() => import('../modules/apps/news/NewsPage'))
  const PrivilegesPage = lazy(() => import('../modules/apps/privilege/PrivilegesPage'))
  const ResultCardsPage = lazy(() => import('../modules/apps/result-card/ResultCardsPage'))
  const RolesPage = lazy(() => import('../modules/apps/role/RolesPage'))
  const SectionsPage = lazy(() => import('../modules/apps/section/SectionsPage'))
  const Scholarship = lazy(() => import('../modules/apps/ss-scholarship/SsScholarshipsPage'))
  const SsDonorPage = lazy(() => import('../modules/apps/ss-donor/SsDonorsPage'))
  const SsDonationPage = lazy(() => import('../modules/apps/ss-donation/SsDonationsPage'))
  const SsCandidatePage = lazy(() => import('../modules/apps/ss-candidate/SsCandidatesPage'))
  const SchoolsPage = lazy(() => import('../modules/apps/school/SchoolsPage'))
  const VehiclesPage = lazy(() => import('../modules/apps/vehicle/VehiclesPage'))
  const RoutesPage = lazy(() => import('../modules/apps/route/RoutesPage'))
  const RouteStopPage = lazy(() => import('../modules/apps/route-stop/RouteStopsPage'))
  const TransportMemberPage = lazy(
    () => import('../modules/apps/transport-member/TransportMembersPage')
  )
  const SlidersPage = lazy(() => import('../modules/apps/slider/SlidersPage'))
  const StudentAttendancesPage = lazy(
    () => import('../modules/apps/student-attendance/StudentAttendancesPage')
  )
  const StudentTypesPage = lazy(() => import('../modules/apps/student-type/StudentTypesPage'))
  const StudentsPage = lazy(() => import('../modules/apps/student/StudentsPage'))
  const SubjectsPage = lazy(() => import('../modules/apps/subject/SubjectsPage'))
  const SyllabussPage = lazy(() => import('../modules/apps/syllabus/SyllabussPage'))
  const ClassLecturesPage = lazy(() => import('../modules/apps/class-lecture/ClassLecturesPage'))
  const PromotionsPage = lazy(() => import('../modules/apps/promotion/PromotionsPage'))
  const IdCardsPage = lazy(() => import('../modules/apps/id-card/IdCardsPage'))
  const IncidentsPage = lazy(() => import('../modules/apps/incident/IncidentsPage'))
  const PaymentsPage = lazy(() => import('../modules/apps/payment/PaymentsPage'))
  const StudyMaterialsPage = lazy(() => import('../modules/apps/study-material/StudyMaterialsPage'))
  const RoutinesPage = lazy(() => import('../modules/apps/routine/RoutinesPage'))
  const VisitorsPage = lazy(() => import('../modules/apps/visitor/VisitorsPage'))
  const PostalDispatchsPage = lazy(
    () => import('../modules/apps/postal-dispatch/PostalDispatchsPage')
  )
  const PostalReceivesPage = lazy(() => import('../modules/apps/postal-receipt/PostalReceiptsPage'))
  const PhoneCallLogsPage = lazy(() => import('../modules/apps/phone-call-log/PhoneCallLogsPage'))
  const VisitorPurpose = lazy(() => import('../modules/apps/visitor-purpose/VisitorPurposesPage'))

  const LeaveApplicationsPage = lazy(
    () => import('../modules/apps/leave-application/LeaveApplicationsPage')
  )
  const LeaveTypesPage = lazy(() => import('../modules/apps/leave-type/LeaveTypesPage'))
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        {/* Pages */}
        <Route
          path='dashboard'
          element={
            <SuspensedView1>
              <DashboardWrapper />
            </SuspensedView1>
          }
        />
        <Route
          path='profile/*'
          element={
            <SuspensedView1>
              <AccountPage />
            </SuspensedView1>
          }
        />
        <Route
          path='apps/admin/academic-year/*'
          element={
            <SuspensedView model='academic-year'>
              <AcademicYearsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/admin/account/*'
          element={
            <SuspensedView model='account'>
              <AccountsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/student-management/admission/*'
          element={
            <SuspensedView model='admission'>
              <AdmissionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/settings/admission-field/*'
          element={
            <SuspensedView model='admission-field'>
              <AdmissionFieldsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/settings/payment-setting/*'
          element={
            <SuspensedView model='payment-setting'>
              <PaymentSettingsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/assignment-submission/*'
          element={
            <SuspensedView model='assignment-submission'>
              <AssignmentSubmissionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/asset-category/*'
          element={
            <SuspensedView model='asset-category'>
              <AssetCategorysPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/asset-item/*'
          element={
            <SuspensedView model='asset-item'>
              <AssetItemsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/asset-purchase/*'
          element={
            <SuspensedView model='asset-purchase'>
              <AssetPurchasesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/asset-issue/*'
          element={
            <SuspensedView model='asset-issue'>
              <AssetIssuesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/store/*'
          element={
            <SuspensedView model='store'>
              <AssetStoresPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/assignment/*'
          element={
            <SuspensedView model='assignment'>
              <AssignmentsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/class/*'
          element={
            <SuspensedView model='class'>
              <ClasssPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/book/*'
          element={
            <SuspensedView model='book'>
              <BooksPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/book-issue/*'
          element={
            <SuspensedView model='book-issue'>
              <BookIssuesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/complain/*'
          element={
            <SuspensedView model='complain'>
              <ComplainsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/discount/*'
          element={
            <SuspensedView model='discount'>
              <DiscountsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/discount/*'
          element={
            <SuspensedView model='discount'>
              <DiscountsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/payment/*'
          element={
            <SuspensedView model='payment'>
              <PaymentsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/employee-management/department/*'
          element={
            <SuspensedView model='department'>
              <DepartmentsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/employee-management/designation/*'
          element={
            <SuspensedView model='designation'>
              <DesignationsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/employee-management/employee-attendance/*'
          element={
            <SuspensedView model='employee-attendance'>
              <EmployeeAttendancesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/employee-management/employee/*'
          element={
            <SuspensedView model='employee'>
              <EmployeesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/exam-attendance/*'
          element={
            <SuspensedView model='exam-attendance'>
              <ExamAttendancesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/expenditure/*'
          element={
            <SuspensedView model='expenditure'>
              <ExpendituresPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam-result/*'
          element={
            <SuspensedView model='exam-result'>
              <ExamResultsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/fee-type/*'
          element={
            <SuspensedView model='invoice'>
              <FeeTypesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/invoice/*'
          element={
            <SuspensedView model='invoice'>
              <InvoicesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/incident-category/*'
          element={
            <SuspensedView model='incident-category'>
              <IncidentCategorysPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/promotion/*'
          element={
            <SuspensedView model='promotion'>
              <PromotionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam-schedule/*'
          element={
            <SuspensedView model='exam-schedule'>
              <ExamSchedulesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/exam-term/*'
          element={
            <SuspensedView model='exam-term'>
              <ExamsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/exam-instruction/*'
          element={
            <SuspensedView model='exam-instruction'>
              <ExamInstructionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/exam-online-exam/*'
          element={
            <SuspensedView model='exam-online-exam'>
              <ExamOnlineExamsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/take-exam/:id'
          element={
            <SuspensedView1>
              <TakeExamPage />
            </SuspensedView1>
          }
        />
        <Route
          path='apps/exam/exam-question/*'
          element={
            <SuspensedView model='exam-question'>
              <ExamQuestionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/exam-taken-exam/*'
          element={
            <SuspensedView model='exam-taken-exam'>
              <ExamTakenExamsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/gallery/*'
          element={
            <SuspensedView model='gallery'>
              <GallerysPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/finalresult/*'
          element={
            <SuspensedView model='final-result'>
              <FinalResultsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/exam/grade/*'
          element={
            <SuspensedView model='grade'>
              <ExamGradesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/id-cards/*'
          element={
            <SuspensedView model='id-card'>
              <IdCardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/incident/*'
          element={
            <SuspensedView model='incident'>
              <IncidentsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/hostel-member/*'
          element={
            <SuspensedView model='hostel-member'>
              <HostelMembersPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/hostel/*'
          element={
            <SuspensedView model='hostel'>
              <HostelsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/leave-application/*'
          element={
            <SuspensedView model='leave-application'>
              <LeaveApplicationsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/leave-type/*'
          element={
            <SuspensedView model='leave-type'>
              <LeaveTypesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/room/*'
          element={
            <SuspensedView model='room'>
              <RoomsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/mark/*'
          element={
            <SuspensedView model='mark'>
              <MarksPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/marksheet/*'
          element={
            <SuspensedView model='marksheet'>
              <MarksheetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/psychomotor/*'
          element={
            <SuspensedView model='psychomotor'>
              <ExamPsychomotorsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/frontend/news/*'
          element={
            <SuspensedView model='news'>
              <NewsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/admin/permission/*'
          element={
            <SuspensedView model='permission'>
              <PrivilegesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/result-card/*'
          element={
            <SuspensedView model='result-card'>
              <ResultCardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/admin/role/*'
          element={
            <SuspensedView model='role'>
              <RolesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/section/*'
          element={
            <SuspensedView model='section'>
              <SectionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/admin/school/*'
          element={
            <SuspensedView model='school'>
              <SchoolsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/frontend/slider/*'
          element={
            <SuspensedView model='slider'>
              <SlidersPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/student-management/student-attendance/*'
          element={
            <SuspensedView model='student-attendance'>
              <StudentAttendancesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/student-management/student-type/*'
          element={
            <SuspensedView model='student-type'>
              <StudentTypesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/student-management/student/*'
          element={
            <SuspensedView model='student'>
              <StudentsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/subject/*'
          element={
            <SuspensedView model='subject'>
              <SubjectsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/study-material/*'
          element={
            <SuspensedView model='studymaterial'>
              <StudyMaterialsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/scholarship/*'
          element={
            <SuspensedView model='scholarship'>
              <Scholarship />
            </SuspensedView>
          }
        />
        <Route
          path='apps/donor/*'
          element={
            <SuspensedView model='donor'>
              <SsDonorPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/donation/*'
          element={
            <SuspensedView model='donation'>
              <SsDonationPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/candidate/*'
          element={
            <SuspensedView model='candidate'>
              <SsCandidatePage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/routine/*'
          element={
            <SuspensedView model='routine'>
              <RoutinesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/syllabus/*'
          element={
            <SuspensedView model='syllabus'>
              <SyllabussPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/academic/class-lecture/*'
          element={
            <SuspensedView model='class-lecture'>
              <ClassLecturesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/vehicle/*'
          element={
            <SuspensedView model='vehicle'>
              <VehiclesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/route/*'
          element={
            <SuspensedView model='route'>
              <RoutesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/route-stop/*'
          element={
            <SuspensedView model='route-stop'>
              <RouteStopPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/transport-member/*'
          element={
            <SuspensedView model='transport-member'>
              <TransportMemberPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/visitor/*'
          element={
            <SuspensedView model='visitor'>
              <VisitorsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/purpose/*'
          element={
            <SuspensedView model='purpose'>
              <VisitorPurpose />
            </SuspensedView>
          }
        />
        <Route
          path='apps/receive/*'
          element={
            <SuspensedView model='receive'>
              <PostalReceivesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/dispatch/*'
          element={
            <SuspensedView model='dispatch'>
              <PostalDispatchsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/calllog/*'
          element={
            <SuspensedView model='calllog'>
              <PhoneCallLogsPage />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren & {model: string}> = ({children, model}) => {
  const {setPage} = usePageContext()
  useEffect(() => {
    setPage(model)
  }, [setPage, model])
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  const {privilege} = useAuth()
  const visible = () => {
    let res = false
    const result = privilege?.find((p) => p.operation?.operation_slug === model)
    if (result) res = result['is_view'] || false
    return res
  }
  return visible() ? (
    <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
  ) : (
    <Routes>
      <Route path='*' element={<Navigate to='/dashboard' />} />
    </Routes>
  )
}
const SuspensedView1: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--kt-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
